

import { Box, Typography, useTheme, TextField, Button, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../AuthContext";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';

const Consumers = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalConsumers, setTotalConsumers] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const { consumers, setConsumers, setValue, fetchData, fetchPumpStats, fetchPumpRunningStatus, fetchPumpSessions, authorizedFetch, formatDuration, deleteConsumer, userRole } = useAuth();
  const canDelete = userRole === "admin" || userRole === "manager";
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const limit = 10;
  const fetchTimeout = useRef(null);
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteConsumer(deleteTarget.mobile_number);
      setSnackbar({ open: true, message: `Consumer ${deleteTarget.name} deleted`, severity: "success" });
      setDeleteTarget(null);
      // If this was the last item on the current page, go back one page
      const isLastOnPage = consumers.length === 1 && currentPage > 1;
      const nextPage = isLastOnPage ? currentPage - 1 : currentPage;
      if (isLastOnPage) setCurrentPage(nextPage);
      fetchConsumersWithParams(nextPage);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
      setDeleteTarget(null);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", minWidth: 40, flex: 0.4 },
    { field: "name", headerName: "Name", minWidth: 120, flex: 0.7 },
    { field: "mobile_number", headerName: "Mobile Number", minWidth: 80, flex: 0.6 },
    { field: "service_number", headerName: "Service Number", minWidth: 120, flex: 0.7 },
    { field: "imei_list", headerName: "Pumps", minWidth: 120, flex: 1 },
    { field: "created_at_ist", headerName: "Registration Time", minWidth: 120, flex: 0.8 },
    { field: "farm_address", headerName: "Farm Address", minWidth: 200, flex: 1.5 },
    ...(canDelete ? [{
      field: "delete",
      headerName: "Delete",
      minWidth: 80,
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => setDeleteTarget(params.row)}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    }] : []),
  ];

 
  const handleRowClick = async (params) => {
    setSelectedRow(params.row);
    setValue(params.row.imei);
    try {
      await Promise.all([
        fetchData(),
        fetchPumpStats(),
        fetchPumpRunningStatus(),
        fetchPumpSessions(),
      ]);
      navigate(`/individualPump/${params.row.imei}`);
    } catch (error) {
      // navigation failure is non-critical
    }
  };




  

  const fetchConsumersWithParams = async (page) => {
    setSearchLoading(true);
    setSearchError(null);
    try {
     
      const searchQuery = searchTerm.trim() ? `&search=${encodeURIComponent(searchTerm)}` : '';
     // console.log('search query consumers wale mein se',searchQuery)
      const response = await authorizedFetch(`/consumers?page=${page}&limit=${limit}${searchQuery}`);
      const consumersLoaded = await response.json();
     // console.log('API response:', consumersLoaded); // Debug API response
      if (response.ok) {
        if (consumersLoaded.consumers.length === 0) {
          setSearchError(searchTerm ? "No Consumers found matching the search criteria" : "No Consumers available");
          setConsumers([]);
          setTotalPages(0);
          setTotalConsumers(0);
        } else {
          const total = consumersLoaded.total || consumersLoaded.consumers.length; // Fallback if total is missing
          setConsumers(consumersLoaded.consumers.map((consumer, index) => ({
            ...consumer,
            id: (page - 1) * limit + index + 1,
            created_at_ist: new Date(consumer.created_at).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
              hour12: true,
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit"
            })
          })));
          setTotalConsumers(total);
          setTotalPages(Math.ceil(total / limit));
          setSearchError(null);
          //console.log(`Set pumps: ${pumpLoaded.pumps.length}, Total: ${total}, Pages: ${Math.ceil(total / limit)}`);
        }
      } else {
        setSearchError("Failed to fetch consumers");
        setConsumers([]);
        setTotalPages(0);
        setTotalConsumers(0);
      }
    } catch (error) {
      console.error("Error fetching consumers:", error);
      setSearchError("Failed to fetch consumers");
      setConsumers([]);
      setTotalPages(0);
      setTotalConsumers(0);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchError("Please enter a valid consumer mobile number or name");
      setSearchTerm('');
      return;
    }
    setCurrentPage(1); // Reset to page 1 on new search
    await fetchConsumersWithParams(1);
   // setSearchTerm(''); // Clear search term after search
  };




  
    useEffect(() => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
      fetchTimeout.current = setTimeout(() => {
        setCurrentPage(1);
        fetchConsumersWithParams(1);
      }, 300);
      return () => {
        if (fetchTimeout.current) {
          clearTimeout(fetchTimeout.current);
        }
      };
    }, [searchTerm]);


  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchConsumersWithParams(nextPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      fetchConsumersWithParams(prevPage);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // Fetch initial pumps on component mount
  useEffect(() => {
    fetchConsumersWithParams(1);
  }, []);

  return (
    <Box m="10px">
      <Header title="CONSUMERS" subtitle="All the Consumers onboard" />
      <Box display="flex" alignItems="center" gap="10px" mb="20px">
        <TextField
          variant="outlined"
          size="small"
          placeholder="Enter consumer name or mobile number to search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{
            width: "40%",
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.primary[400],
              "& fieldset": {
                borderColor: colors.grey[100],
              },
              "&:hover fieldset": {
                borderColor: colors.blueAccent[300],
              },
            },
            "& .MuiInputBase-input": {
              color: colors.grey[100],
            },
          }}
        />
         {searchLoading && <CircularProgress size={20} color="primary" />}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={searchLoading}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
            fontSize: "0.875rem",
            padding: "8px 16px",
          }}
        >
          {searchLoading ? <CircularProgress size={20} color="inherit" /> : "Search"}
        </Button>
      </Box>
      {searchError && (
        <Typography color="error" mb="10px">
          {searchError}
        </Typography>
      )}
        <Box
         m="20px 0 0 0"
         height="70vh"
         sx={{
           width: '100%',
           overflow: 'hidden', // Prevent any overflow on the container
           "& .MuiDataGrid-root": {
             border: "none",
             width: '100%',
             height: '100%',
             overflow: 'hidden', // Prevent root overflow
           },
           "& .MuiDataGrid-main": {
             overflow: 'hidden',
           },
           "& .MuiDataGrid-columnHeaders": {
             backgroundColor: colors.blueAccent[700],
             borderBottom: "none",
             fontSize: '0.9rem',
             position: 'sticky',
             top: 0,
             zIndex: 2,
             width: '100% !important', // Force full width
             minWidth: '100% !important',
             marginBottom:'-55px'
             // Remove the negative margin
           },
           "& .MuiDataGrid-columnHeadersInner": {
             backgroundColor: colors.blueAccent[700],
             width: '100% !important',
             // Compensate for scrollbar width
             paddingRight: '0px !important',
           },
           "& .MuiDataGrid-virtualScroller": {
             backgroundColor: colors.primary[400],
             overflow: 'auto !important',
             // Hide horizontal scrollbar
             overflowX: 'hidden !important',
             overflowY: 'auto !important',
             // Custom scrollbar styling
             '&::-webkit-scrollbar': {
               width: '8px',
             },
             '&::-webkit-scrollbar-track': {
               backgroundColor: colors.primary[400],
             },
             '&::-webkit-scrollbar-thumb': {
               backgroundColor: colors.blueAccent[600],
               borderRadius: '4px',
             },
             '&::-webkit-scrollbar-thumb:hover': {
               backgroundColor: colors.blueAccent[500],
             },
           },
           "& .MuiDataGrid-virtualScrollerContent": {
             height: 'auto !important',
             width: '100% !important',
           },
           "& .MuiDataGrid-virtualScrollerRenderZone": {
             width: '100% !important',
           },
           "& .MuiDataGrid-cell": {
             borderBottom: "none",
             fontSize: '0.875rem',
             whiteSpace: 'normal !important',
             wordWrap: 'break-word !important',
             lineHeight: '1.4 !important',
             padding: '8px !important',
             overflow: 'hidden', // Prevent cell content overflow
           },
           "& .MuiDataGrid-row": {
             width: '100% !important',
             maxWidth: '100% !important',
           },
           "& .MuiDataGrid-row:hover": {
             backgroundColor: colors.blueAccent[800],
           },
           "& .MuiDataGrid-footerContainer": {
             borderTop: "none",
             backgroundColor: colors.blueAccent[700],
             position: 'sticky',
             bottom: 0,
             zIndex: 1,
           },
           "& .MuiDataGrid-columnHeaderTitle": {
             whiteSpace: 'normal',
             lineHeight: '1.2',
             overflow: 'hidden',
             textOverflow: 'ellipsis',
           },
           // Hide default scrollbars on the entire grid
           "& .MuiDataGrid-scrollArea": {
             display: 'none',
           },
           "& .MuiDataGrid-scrollArea--left": {
             display: 'none',
           },
           "& .MuiDataGrid-scrollArea--right": {
             display: 'none',
           },
         }}
       >
         <DataGrid
           rows={consumers}
           columns={columns}
           disableSelectionOnClick
           disableExtendRowFullWidth
           hideFooterPagination
           hideFooterSelectedRowCount
           disableColumnResize // Prevent column resizing that might cause horizontal scroll
           disableColumnMenu // Optional: disable column menu
           sx={{
             '& .MuiDataGrid-columnHeader': {
               outline: 'none !important',
             },
             '& .MuiDataGrid-cell': {
               outline: 'none !important',
             },
           }}
         />
       </Box>
      <Box display="flex" justifyContent="center" alignItems="center" gap="10px" mt="10px">
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrevPage}
          disabled={searchLoading || currentPage === 1}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
          }}
        >
          Previous
        </Button>
        <Typography>
          Page {currentPage} of {totalPages} (Total Consumers: {totalConsumers})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextPage}
          disabled={searchLoading || currentPage === totalPages || totalPages === 0}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
          }}
        >
          Next
        </Button>
      </Box>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Consumer</DialogTitle>
        <DialogContent>
          <Typography>
            Delete consumer <strong>{deleteTarget?.name}</strong> ({deleteTarget?.mobile_number})?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            This will also unlink their pumps and delete their mobile app account. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Consumers;